import React, { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, Users, Mail, X } from "lucide-react";
import baseApi from "utils/axios-instance";
import { Mixpanel } from "utils/Analytics";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Toaster } from "components/ui/sonner";

interface TeamMember {
  user_id?: string;
  email: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  status: "active" | "pending";
  picture_url: string | null;
  is_current_user?: boolean;
}

function TeamsPage() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [showAddTeamMemberDialog, setShowAddTeamMemberDialog] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [addingTeamMember, setAddingTeamMember] = useState(false);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [revokingEmail, setRevokingEmail] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [showRemoveConfirmDialog, setShowRemoveConfirmDialog] = useState(false);

  const fetchTeamMembers = useCallback(async () => {
    setLoadingTeamMembers(true);
    try {
      const response = await baseApi.get("/team_members", { withCredentials: true });
      setTeamMembers(response.data.team_members || []);
    } catch (error: any) {
      console.error("Error fetching team members:", error);
      toast.error("Failed to load team members.", {
        description: error.response?.data?.detail || "An unexpected error occurred.",
      });
    } finally {
      setLoadingTeamMembers(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const handleAddTeamMember = async () => {
    if (!newMemberEmail.trim()) {
      toast.error("Please enter an email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setAddingTeamMember(true);
    try {
      await baseApi.post(
        "/team_members",
        { email: newMemberEmail.trim() },
        { withCredentials: true }
      );
      toast.success("Team member invited successfully!", {
        description: "They will be added to your team once they attempt to login.",
      });
      setNewMemberEmail("");
      setShowAddTeamMemberDialog(false);
      fetchTeamMembers();
      Mixpanel.track("Team Member Added");
    } catch (error: any) {
      Mixpanel.track("Error: Team Member Add", { error: error.message });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An unexpected error occurred.";
      toast.error(message, {
        description: "Failed to add team member.",
      });
      console.error("Add team member error:", error);
    } finally {
      setAddingTeamMember(false);
    }
  };

  const handleRevokeInvitation = async (email: string) => {
    setRevokingEmail(email);
    try {
      const encodedEmail = encodeURIComponent(email);
      await baseApi.delete(`/team_members/${encodedEmail}`, {
        withCredentials: true,
      });
      toast.success("Invitation revoked successfully!", {
        description: "The team member will no longer be able to join your team.",
      });
      fetchTeamMembers();
      Mixpanel.track("Team Member Invitation Revoked");
    } catch (error: any) {
      Mixpanel.track("Error: Team Member Revoke", { error: error.message });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An unexpected error occurred.";
      toast.error(message, {
        description: "Failed to revoke invitation.",
      });
      console.error("Revoke invitation error:", error);
    } finally {
      setRevokingEmail(null);
    }
  };

  const handleRemoveTeamMemberClick = (member: TeamMember) => {
    setMemberToRemove(member);
    setShowRemoveConfirmDialog(true);
  };

  const handleConfirmRemoveTeamMember = async () => {
    if (!memberToRemove) return;

    setRemovingEmail(memberToRemove.email);
    try {
      const encodedEmail = encodeURIComponent(memberToRemove.email);
      await baseApi.delete(`/team_members/${encodedEmail}`, {
        withCredentials: true,
      });
      toast.success("Team member removed successfully!", {
        description: "They will no longer have access to this team's account.",
      });
      setShowRemoveConfirmDialog(false);
      setMemberToRemove(null);
      fetchTeamMembers();
      Mixpanel.track("Team Member Removed");
    } catch (error: any) {
      Mixpanel.track("Error: Team Member Remove", { error: error.message });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An unexpected error occurred.";
      toast.error(message, {
        description: "Failed to remove team member.",
      });
      console.error("Remove team member error:", error);
    } finally {
      setRemovingEmail(null);
    }
  };

  return (
    <div className="w-full">
      <Toaster richColors position="top-center" />
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#005151] rounded-xl">
                <Users className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Team Members</h1>
                <p className="text-gray-600 mt-1">
                  Manage who can access your team's account
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowAddTeamMemberDialog(true)}
              className="bg-[#005151] hover:bg-[#004040] text-white"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Add Team Member
            </Button>
          </div>
        </div>

        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-gray-900">
              Your Team
            </CardTitle>
            <CardDescription className="text-gray-600">
              Active members and pending invitations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingTeamMembers ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-[#005151] mr-2" />
                <span className="text-gray-600">Loading team members...</span>
              </div>
            ) : !teamMembers || teamMembers.length === 0 ? (
              <div className="text-center py-12">
                <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No team members yet
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">
                  Invite team members to collaborate on your account. They'll be able to log in once invited.
                </p>
                <Button
                  variant="outline"
                  className="border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
                  onClick={() => setShowAddTeamMemberDialog(true)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add Your First Team Member
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {teamMembers.map((member, index) => (
                  <div
                    key={`${member.email}-${index}`}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      {member.picture_url ? (
                        <Avatar className="h-10 w-10 flex-shrink-0">
                          <AvatarImage
                            src={member.picture_url}
                            alt={member.name || member.email}
                          />
                          <AvatarFallback className="text-xs bg-gray-100 text-[#005151] font-medium">
                            {member.name
                              ? member.name.charAt(0).toUpperCase()
                              : member.email.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-[#005151]/10 flex items-center justify-center flex-shrink-0">
                          {member.name ? (
                            <span className="text-sm font-medium text-[#005151]">
                              {member.name.charAt(0).toUpperCase()}
                            </span>
                          ) : (
                            <Mail className="h-5 w-5 text-[#005151]" />
                          )}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900 truncate">
                            {member.name || member.email}
                          </p>
                          {member.status === "pending" && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex-shrink-0">
                              Pending
                            </span>
                          )}
                          {member.status === "active" && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full flex-shrink-0">
                              {member.is_current_user ? "You" : "Active"}
                            </span>
                          )}
                        </div>
                        {member.name && member.email !== member.name && (
                          <p className="text-sm text-gray-500 truncate">{member.email}</p>
                        )}
                      </div>
                    </div>
                    {member.status === "pending" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeInvitation(member.email)}
                        disabled={revokingEmail === member.email}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 ml-2"
                      >
                        {revokingEmail === member.email ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Revoking...
                          </>
                        ) : (
                          <>
                            <X className="h-4 w-4 mr-2" />
                            Revoke
                          </>
                        )}
                      </Button>
                    )}
                    {member.status === "active" && !member.is_current_user && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveTeamMemberClick(member)}
                        disabled={removingEmail === member.email}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 ml-2"
                      >
                        {removingEmail === member.email ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Removing...
                          </>
                        ) : (
                          <>
                            <X className="h-4 w-4 mr-2" />
                            Remove
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showAddTeamMemberDialog} onOpenChange={setShowAddTeamMemberDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Enter the email address of the person you'd like to add to your team. They'll be able to log in once invited.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="team-member-email" className="text-sm font-medium text-gray-700">
                Email Address
              </label>
              <Input
                id="team-member-email"
                type="email"
                placeholder="colleague@example.com"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !addingTeamMember) {
                    handleAddTeamMember();
                  }
                }}
                className="h-12"
                disabled={addingTeamMember}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddTeamMemberDialog(false);
                setNewMemberEmail("");
              }}
              disabled={addingTeamMember}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddTeamMember}
              disabled={addingTeamMember || !newMemberEmail.trim()}
              className="bg-[#005151] hover:bg-[#004040] text-white"
            >
              {addingTeamMember ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Team Member
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showRemoveConfirmDialog}
        onOpenChange={(open) => {
          setShowRemoveConfirmDialog(open);
          if (!open) {
            setMemberToRemove(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-medium text-gray-900">
                {memberToRemove?.name || memberToRemove?.email}
              </span>{" "}
              from your team? They will lose access to this account and its data.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveConfirmDialog(false);
                setMemberToRemove(null);
              }}
              disabled={!!removingEmail}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemoveTeamMember}
              disabled={!!removingEmail}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {removingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove from Team"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TeamsPage;
